import { gameInfoType } from '@op/types/common';
import React, { useEffect, useRef, useState } from 'react';
import useRenderComponent from '@op/components/FilterCommon/mainComponents/FilterBox/useRenderComponent';
import { getLocale } from 'umi';
import CardCommon from '@op/components/CardCommon';
import { arrayToObj } from '@op/utils';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import './index.scss';
import { compareParams } from '@op/utils/data';
import { ServerTimeType } from '@op/components/FilterCommon/types';

export type PeriodType = {
  isPeriod: boolean;
  startPeriod: number;
  endPeriod: number;
};

export default function FilterInfo({
  gameInfo,
  userInfo,
  children,
  wholeGameInfo,
  onClear
}: {
  gameInfo: gameInfoType;
  userInfo: any;
  children: (value: {
    filter: any;
    metrics: Array<string>;
    gameData: { [key: string]: any };
    dateType: ServerTimeType;
    period: PeriodType;
    hotContentFilter: any;
  }) => JSX.Element;
  wholeGameInfo: any;
  onClear: () => void;
}) {
  const isFirst = useRef(true);
  const filterRef = useRef<any>(null);
  const [filter, setFilter] = useState(null);
  const [metrics, setMetrics] = useState<Array<string>>([]);
  const [gameData, setGameData] = useState<{ [key: string]: any }>({});
  const [dateType, setDateType] = useState<ServerTimeType>('daily');
  const [period, setPeriod] = useState<PeriodType>({
    isPeriod: false,
    startPeriod: 1,
    endPeriod: 30,
  });
  const [hotContentFilter, setHotContentFilter] = useState(null);
  function countPeriodTime(data: any) {
    const {
      gameSelection,
      startPeriod,
      endPeriod,
      releaseType,
      start_time,
      end_time,
      date_type,
      isRelease,
    } = data;

    if (!isRelease) {
      const games = gameSelection.map((item: any) => {
        const { edition_unified_id, id_type, entity_type } = item;
        return {
          start_time,
          end_time,
          date_type,
          edition_unified_id,
          id_type,
          entity_type,
        };
      });

      return { games, dateType: date_type };
    }

    const dateType = releaseType === 'days' ? 'daily' : 'monthly';
    const games = gameSelection.map((item: any) => {
      const { edition_unified_id, id_type, entity_type } = item;
      let sd = '0000-00-00 00:00:00';
      let ed = '0000-00-00 00:00:00';
      if (item.release_time) {
        sd = moment(item.release_time)
          .add(startPeriod - 1, releaseType)
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss');
        ed = moment(item.release_time)
          .add(endPeriod - 1, releaseType)
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ss');
      }

      return {
        start_time: sd,
        end_time: ed,
        date_type: dateType,
        edition_unified_id,
        id_type,
        entity_type,
      };
    });

    return { games, dateType };
  }

  function dealwithFilterData() {
    let data = cloneDeep(filterRef.current);

    const {
      gameSelection,
      metrics,
      startPeriod,
      endPeriod,
      releaseType,
      isRelease,
      ...otherFilter
    } = data;

    const gameData = arrayToObj(gameSelection, 'edition_unified_id');

    const { games, dateType } = countPeriodTime(data);

    setPeriod({
      isPeriod: isRelease,
      startPeriod,
      endPeriod,
    });
    setDateType(dateType);
    setFilter({
      ...gameInfo,
      ...otherFilter,
      compare_games: games,
      metrics,
      is_release: isRelease,
    });
    setHotContentFilter({
      ...gameInfo,
      ...otherFilter,
    });
    onClear();
    setGameData(gameData);
    setMetrics(metrics);
  }

  const { renderComponentElement, loading } = useRenderComponent({
    filter: [
      'compareGamesSelection',
      'compareDate',
      'metrics',
      'regionAndLanguage',
      'channel',
      'sentiment',
    ],
    confirm: (data) => {
      if (isFirst.current) {
        filterRef.current = data.value;
        isFirst.current = false;
        dealwithFilterData();
      }
    },
    changeOnCloseWindow: (value, deleteKeys) => {
      if (!isFirst.current && filterRef.current !== null) {
        if (compareParams(value, filterRef.current)) return;

        filterRef.current = { ...filterRef.current, ...value };

        if (deleteKeys) {
          const keys = Array.isArray(deleteKeys) ? deleteKeys : [deleteKeys];

          keys.forEach((key) => {
            if (
              Object.prototype.hasOwnProperty.call(filterRef.current || {}, key)
            ) {
              delete filterRef.current[key];
            }
          });
        }

        dealwithFilterData();
      }
    },
    gameInfo,
    lang: getLocale(),
    defaultValue: {},
    userInfo,
    isTile: true,
    wholeGameInfo,
    defaultConfig: {
      regionAndLanguage: {
        toggleLanguage: false,
      },
    },
  });


  return (
    <>
      <div className="dbo-compare-filter">{renderComponentElement}</div>
      <CardCommon contentCard loading={loading} loadingHeight={750}>
        {children({ filter, metrics, gameData, dateType, period, hotContentFilter })}
      </CardCommon>
    </>
  );
}
